/**
 * SpreadsTotals — moneyline-style table view for spread + total markets.
 * One row per canonical game expanded to 4 perspective rows (away spread,
 * home spread, over total, under total). See SpreadsTotalsTable for the
 * column structure and ladder convention.
 *
 * Owns:
 *   - Per-event selected line indices for spread + total chip strips
 *   - Which row (if any) is currently expanded as a ladder
 *   - Orderbook subscription needs (only the selected line of the expanded
 *     row per event, so we don't subscribe every line on every game)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { useAccountStore } from '@/stores/accountStore';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { toHydraGameKey } from '@/services/analyticsGameKey';
import type {
  GameEventRow,
  GameRow,
  Sport,
  StatusFilter,
  DateFilter,
  MarketSide,
  PriceMode,
  SpreadLine,
  TotalLine,
} from '../ValueDashboard/types';
import type { TradeModalData } from '../ValueDashboard/TradeModal';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';
import { SpreadsTotalsTable, type RowKind } from './SpreadsTotalsTable';
import { linePriceCents, synthesizeFromSpread, synthesizeFromTotal } from './synthesize';
import { buildBetLineContext } from './buildBetLineContext';

interface SpreadsTotalsProps {
  sport: Sport;
  statusFilter: StatusFilter;
  dateFilter: DateFilter;
  selectedDateIso: string;
  /** Toolbar YES/NO toggle — drives pill highlight and ladder side. */
  marketSide: MarketSide;
  /** Toolbar Maker/Taker/Market toggle — drives the collapsed pill price.
   *  Pills read via `pillPriceCents(book, side, priceMode)` to match the
   *  moneyline GameTable behaviour. */
  priceMode: PriceMode;
  /**
   * Fired with the synthesized GameRow whenever a spread or total row is
   * expanded, or null when it collapses. Parent uses it to render the
   * right-side detail panel.
   */
  onActiveGameChange?: (game: GameRow | null) => void;
  /**
   * Fired alongside `onActiveGameChange` with the bet-description title +
   * chip strip context, so the detail panel can render the alternate
   * header for spread/total bets. Null when no row is expanded.
   */
  onActiveBetLineContextChange?: (context: BetLineContext | null) => void;
  /**
   * Fired when the user clicks an individual Y/N pill — parent opens the
   * existing TradeModal popup.
   */
  onTradeClick?: (data: TradeModalData) => void;
  /**
   * Counter that bumps when the parent's detail panel closes. Tells this
   * view to collapse its inline expansion state so re-opening the panel
   * doesn't require two clicks.
   */
  closeSignal?: number;
}

/** Index of the line whose primary venue YES priceCents is closest to 50¢.
 *  Determines the *default* chip when the strip first renders for a game;
 *  the user can navigate to any other line. */
export function defaultLineIndex(
  lines: Array<SpreadLine | TotalLine>,
): number {
  if (lines.length === 0) return 0;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < lines.length; i++) {
    const cents = linePriceCents(lines[i]);
    if (cents == null) continue;
    const dist = Math.abs(cents - 50);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function SpreadsTotals({
  sport,
  statusFilter,
  dateFilter,
  selectedDateIso,
  marketSide,
  priceMode,
  onActiveGameChange,
  onActiveBetLineContextChange,
  onTradeClick,
  closeSignal = 0,
}: SpreadsTotalsProps) {
  const connected = useMarketStore((s) => s.connected);
  const getGameEvents = useMarketStore((s) => s.getGameEvents);
  const version = useMarketStore((s) => s.version);

  const rows = useMemo(() => {
    void version;
    return getGameEvents(sport, { statusFilter, dateFilter, selectedDateIso });
  }, [getGameEvents, sport, statusFilter, dateFilter, selectedDateIso, version]);

  // Batched Unabated gameState poll for the visible event rows. Drives
  // the period status text + per-team scores in MatchupRow once a game
  // starts. Pulls from the same hydra endpoint the moneyline page
  // polls; ValueDashboard already polls when the moneyline tab is up,
  // but the spread/total tab needs its own subscription scoped to its
  // events (which can be a subset/superset of the dashboard view).
  const eventGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const k = toHydraGameKey(r.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [rows]);
  useGameStatesPoll(eventGameKeys);

  // Per-event selection state. Maps live across re-derives so user choices
  // survive RAF ticks; new rows get a closest-50¢ default.
  const [spreadIdxByEvent, setSpreadIdxByEvent] = useState<Map<string, number>>(new Map());
  const [totalIdxByEvent, setTotalIdxByEvent] = useState<Map<string, number>>(new Map());
  const [expandedRowByEvent, setExpandedRowByEvent] = useState<Map<string, RowKind | null>>(new Map());

  useEffect(() => {
    setSpreadIdxByEvent((prev) => seedDefaults(prev, rows, (r) => r.spreads, defaultLineIndex));
    setTotalIdxByEvent((prev) => seedDefaults(prev, rows, (r) => r.totals, defaultLineIndex));
    setExpandedRowByEvent((prev) => pruneExpanded(prev, rows));
  }, [rows]);

  // Orderbook needs — the currently-selected spread AND total ticker per
  // event, even when collapsed. Pills use `pillPriceCents` which derives
  // best ask from the opposite side's bid stream, mirroring the moneyline
  // GameTable behaviour and matching Kalshi's quick-view ask prices. Cost
  // scales with rendered events × 2 strips × 2 venues, not Σ lines.
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const spread = row.spreads[spreadIdxByEvent.get(row.key) ?? 0];
      const total = row.totals[totalIdxByEvent.get(row.key) ?? 0];
      if (spread?.kalshi?.marketTicker) set.add(spread.kalshi.marketTicker);
      if (spread?.polymarket?.marketTicker) set.add(spread.polymarket.marketTicker);
      if (total?.kalshi?.marketTicker) set.add(total.kalshi.marketTicker);
      if (total?.polymarket?.marketTicker) set.add(total.polymarket.marketTicker);
    }
    return Array.from(set);
  }, [rows, spreadIdxByEvent, totalIdxByEvent]);

  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('spreads-totals', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('spreads-totals', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Stable references: select arrays directly; the table walks them for
  // per-event strip aggregation. Don't `.map()` inside the selector —
  // that mints a new array every render and infinite-loops.
  const openOrders = useAccountStore((s) => s.openOrders);
  const accountPositions = useAccountStore((s) => s.positions);

  // Snapshot filter is ONLY the user's own tickers (open orders +
  // positions). PositionTracker does the same — keeps Kalshi's
  // /portfolio/orders/queue_positions call narrow, which is the only
  // way queue depth comes back reliably. Pre-spread/total fix, we sent
  // every visible spread/total ticker to the snapshot (hundreds across
  // sports + events) and Kalshi's response silently dropped queue data
  // for some tickers — chips showed `count/?`. C/O ring + position
  // aggregation reads accountStore directly, so they don't need
  // visible tickers in the snapshot.
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const o of openOrders) if (o.ticker) set.add(o.ticker);
    for (const p of accountPositions) if (p.ticker) set.add(p.ticker);
    return Array.from(set);
  }, [openOrders, accountPositions]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);

  // Chip-strip scrolls implicitly activate the row they're on. Otherwise
  // a trader scrolling chips on row B while row A is still expanded would
  // see the side panel keep rendering A — confusing because the chip
  // they're touching IS visibly highlighting on B. Mirrors the
  // "only one expanded across all events" rule handleToggleExpand
  // enforces: this row's kind becomes the sole expansion.
  const handleSelectSpreadIdx = useCallback((eventKey: string, idx: number) => {
    setSpreadIdxByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, idx);
      return next;
    });
    setExpandedRowByEvent(() => new Map<string, RowKind | null>([[eventKey, 'spread']]));
  }, []);

  const handleSelectTotalIdx = useCallback((eventKey: string, idx: number) => {
    setTotalIdxByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, idx);
      return next;
    });
    setExpandedRowByEvent(() => new Map<string, RowKind | null>([[eventKey, 'total']]));
  }, []);

  const handleToggleExpand = useCallback((eventKey: string, row: RowKind) => {
    setExpandedRowByEvent((prev) => {
      // Only one row across all events can be expanded at a time — clicking
      // a spread/total in event B closes whatever was open in event A. The
      // parent detail panel renders a single context and the active-row
      // effect picks the first match in `rows` order, which silently
      // strands stale expansions if we let multiple events stay open.
      const wasExpanded = prev.get(eventKey) === row;
      const next = new Map<string, RowKind | null>();
      if (!wasExpanded) next.set(eventKey, row);
      return next;
    });
  }, []);

  // Parent closes its panel → collapse our inline expansion so the next
  // click on the same row re-opens the panel cleanly. Skip the very first
  // run (closeSignal=0 at mount).
  useEffect(() => {
    if (closeSignal === 0) return;
    setExpandedRowByEvent(new Map());
  }, [closeSignal]);

  // Notify parent of the currently-active (event, market-type) → synthesized
  // GameRow so the right-side detail panel can render. Also fires the
  // matching BetLineContext so the panel can render its alternate header
  // (bet-description title + chip strip). Both fire `null` when no row is
  // expanded across any event.
  useEffect(() => {
    let active: GameRow | null = null;
    let context: BetLineContext | null = null;
    for (const row of rows) {
      const expanded = expandedRowByEvent.get(row.key);
      if (!expanded) continue;
      if (expanded === 'spread') {
        const idx = spreadIdxByEvent.get(row.key) ?? 0;
        const line = row.spreads[idx];
        if (line) {
          active = synthesizeFromSpread(row, line);
          context = buildBetLineContext({
            kind: 'spread',
            event: row,
            lines: row.spreads,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectSpreadIdx(row.key, next),
          });
        }
      } else {
        const idx = totalIdxByEvent.get(row.key) ?? 0;
        const line = row.totals[idx];
        if (line) {
          active = synthesizeFromTotal(row, line);
          context = buildBetLineContext({
            kind: 'total',
            event: row,
            lines: row.totals,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectTotalIdx(row.key, next),
          });
        }
      }
      break; // only one active expansion across all events
    }
    onActiveGameChange?.(active);
    onActiveBetLineContextChange?.(context);
  }, [
    rows,
    expandedRowByEvent,
    spreadIdxByEvent,
    totalIdxByEvent,
    onActiveGameChange,
    onActiveBetLineContextChange,
    handleSelectSpreadIdx,
    handleSelectTotalIdx,
  ]);

  const handlePillTrade = useCallback(
    (
      eventKey: string,
      rowKind: RowKind,
      venue: 'kalshi' | 'polymarket',
      side: 'yes' | 'no',
      cents: number,
    ) => {
      // Make sure the inline ladder + right-side panel reflect this line.
      setExpandedRowByEvent((prev) => {
        const next = new Map(prev);
        next.set(eventKey, rowKind);
        return next;
      });

      if (!onTradeClick) return;
      const event = rows.find((r) => r.key === eventKey);
      if (!event) return;
      const line =
        rowKind === 'spread'
          ? event.spreads[spreadIdxByEvent.get(eventKey) ?? 0]
          : event.totals[totalIdxByEvent.get(eventKey) ?? 0];
      if (!line) return;

      const game =
        rowKind === 'spread'
          ? synthesizeFromSpread(event, line as SpreadLine)
          : synthesizeFromTotal(event, line as TotalLine);

      onTradeClick({
        game,
        teamSide: 'away', // synthetic GameRow has identical away/home books
        venue,
        prefillPrice: cents,
        initialOrderSide: side,
        postOnly: false,
      });
    },
    [rows, spreadIdxByEvent, totalIdxByEvent, onTradeClick],
  );

  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
        {connected ? (
          <span>No {sport.toUpperCase()} spreads or totals</span>
        ) : (
          <>
            <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
            <span>Connecting to Hydra...</span>
          </>
        )}
      </div>
    );
  }

  return (
    <SpreadsTotalsTable
      rows={rows}
      marketSide={marketSide}
      priceMode={priceMode}
      spreadIdxByEvent={spreadIdxByEvent}
      totalIdxByEvent={totalIdxByEvent}
      expandedRowByEvent={expandedRowByEvent}
      positionByTicker={userActivity.positionByTicker}
      ordersByTicker={userActivity.ordersByTicker}
      openOrders={openOrders}
      onSelectSpreadIdx={handleSelectSpreadIdx}
      onSelectTotalIdx={handleSelectTotalIdx}
      onToggleExpand={handleToggleExpand}
      onPillTrade={handlePillTrade}
    />
  );
}

// ── State helpers ─────────────────────────────────────────────────────

function seedDefaults<T>(
  prev: Map<string, number>,
  rows: GameEventRow[],
  pick: (r: GameEventRow) => T[],
  defaultFn: (lines: T[]) => number,
): Map<string, number> {
  let changed = false;
  const next = new Map<string, number>();
  for (const row of rows) {
    const lines = pick(row);
    const existing = prev.get(row.key);
    if (existing != null) {
      const clamped = Math.min(existing, Math.max(lines.length - 1, 0));
      next.set(row.key, clamped);
      if (clamped !== existing) changed = true;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next.set(row.key, defaultFn(lines as any));
      changed = true;
    }
  }
  if (!changed && prev.size === next.size) return prev;
  return next;
}

function pruneExpanded(
  prev: Map<string, RowKind | null>,
  rows: GameEventRow[],
): Map<string, RowKind | null> {
  const live = new Set(rows.map((r) => r.key));
  const next = new Map<string, RowKind | null>();
  let changed = prev.size !== live.size;
  for (const [k, v] of prev) {
    if (live.has(k)) next.set(k, v);
    else changed = true;
  }
  if (!changed) return prev;
  return next;
}

// Re-export shape types for tests / future detail-page reuse.
export type { GameEventRow, SpreadLine, TotalLine };
